import { z } from 'zod';

import { apiFetch } from '@/lib/api-client';

/**
 * Ask BankIQ NLQ API client (SCRUM-83) over the SCRUM-85 backend. The
 * /api/intelligence/nlq/* surface is TENANT-SCOPED — every call carries
 * X-Tenant-Id (same convention as settings-api / vault-api).
 *
 * Answers are REAL watsonx output grounded exclusively in the tenant's data
 * data snapshot (never live queries). `aiGenerated: false` means the gateway
 * was unavailable or the model output was unusable — the caller renders the
 * honest fallback message (the endpoint never 500s for that case).
 *
 * AUTHORIZATION LIMITATION (plan RT5): the Bank Executive gate is UI-only
 * (nav visibility + the page's denied state) until SCRUM-103 lands server-side
 * role enforcement — the same demo-era class as every /api/* surface.
 */
const ASK_URL = '/api/intelligence/nlq/ask';
const SNAPSHOT_INFO_URL = '/api/intelligence/nlq/snapshot-info';
// ↑ Swap-point: nginx proxies /api/* to bankiq-api in the deployed container.

// Tenant ID for the Meridian Community Bank tenant (seeded by bankiq-db-seed CE Job).
// SCRUM-643: no hard-coded tenant header — the server derives the tenant from
// the authenticated App ID token's tenant_id claim (SCRUM-102). apiFetch carries
// the Bearer; a client X-Tenant-Id is ignored for authenticated callers.
const tenantHeader = {} as const;
const jsonHeaders = { 'Content-Type': 'application/json' } as const;

/** Mirror of the server's NlqLimits.MaxQuestionLength (server-enforced too). */
export const MAX_QUESTION_LENGTH = 500;

const askResponseSchema = z.object({
  answerMarkdown: z.string(),
  confidence: z.enum(['high', 'medium', 'low']),
  followups: z.array(z.string()),
  source: z.string(),
  snapshotAt: z.string().nullable(),
  aiGenerated: z.boolean(),
  model: z.string(),
});

export type NlqAskResponse = z.infer<typeof askResponseSchema>;

const snapshotInfoSchema = z.object({
  generatedAt: z.string().nullable(),
});

export type NlqSnapshotInfo = z.infer<typeof snapshotInfoSchema>;

/**
 * POST /api/intelligence/nlq/ask. Throws on transport/HTTP/shape errors;
 * resolves with `aiGenerated: false` for the server's unavailable envelope —
 * both paths render the same client-side fallback message (nlq-store).
 */
export async function askNlq(question: string): Promise<NlqAskResponse> {
  const res = await apiFetch(ASK_URL, {
    method: 'POST',
    headers: jsonHeaders,
    body: JSON.stringify({ question }),
  });
  if (!res.ok) throw new Error('Failed to reach Ask BankIQ.');
  return askResponseSchema.parse(await res.json());
}

/** GET /api/intelligence/nlq/snapshot-info — freshness for the dock line. */
export async function fetchNlqSnapshotInfo(): Promise<NlqSnapshotInfo> {
  const res = await apiFetch(SNAPSHOT_INFO_URL, { headers: tenantHeader });
  if (!res.ok) throw new Error('Failed to load snapshot info.');
  return snapshotInfoSchema.parse(await res.json());
}

/** "Apr 22, 2026"-style label for the dock's snapshot line (UTC, en-US). */
export function formatSnapshotDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}
